import { useState } from "react";
import { UnorderedList, ListItem } from "@chakra-ui/react";

import MetaHead from "components/base/MetaHead";
import MainContainer from "components/sections/MainContainer";
import VerticalImageSection from "components/sections/VerticalImageSection";
import TextHeader from "components/sections/TextHeader";
import TextColumns from "components/sections/TextColumns";
import NavFooter from "components/sections/NavFooter";
import ImageSection from "components/sections/ImageSection";
import CardSection from "components/sections/CardSection";
import FaqSection from "components/sections/FaqSection";
import FloatingCTA from "components/blocks/FloatingCTA";
import Text from "components/base/Text";
import Card from "components/blocks/Card";
import Trans from "components/base/Trans";
import {
  MotionBox as Box,
  MotionContainer as Container,
  staggeredAppear,
  appearBelow,
} from "utils/motion";
import { useShowCTA } from "utils/hooks";
import { getFaqsByPage } from "lib/payloadApi";

export default function Interiors({ faqCategories, faqSchema }) {
  const [lastSectionVisible, setLastSectionVisible] = useState(false);
  const { headerRef, showCTA } = useShowCTA(lastSectionVisible);

  return (
    <MainContainer
      navStyle={{ gradient: false, inverted: false }}
      updateVisible={setLastSectionVisible}
    >
      <MetaHead
        title="LBL_GA2_META_TITLE"
        keywords={["LBL_META_KEYWORD_4"]}
        description="LBL_GA2_META_DESC"
        imageUrl="/images/meta/ga.jpg"
        structuredData={faqSchema}
      />
      <div ref={headerRef}>
        <TextHeader
          page={<Trans id="LBL_GA2_PAGE_TITLE" />}
          title={<Trans id="LBL_GA2_TITLE" />}
          inverted
        />
      </div>
      <ImageSection
        image="/images/ga/header.jpg"
        alt="HX50 Header - Hill helicopters"
        bg="black.700"
      />
      <Box bg="black.900" width="100%">
        <Container>
          <Box
            variants={staggeredAppear}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            maxW={["100%", null, "80%"]}
          >
            <Text
              type="headline_md"
              variants={appearBelow}
              mb="lg"
              color="white"
            >
              <Trans id="LBL_GA2_APPROACH_TITLE" />
            </Text>
            <Text type="p_md" variants={appearBelow} color="white">
              <Trans id="LBL_GA2_APPROACH_DESCRIPTION" />
            </Text>
          </Box>
        </Container>

        <Box bg="black.900" color="white" width="100%">
          <TextColumns
            bg="black.900"
            title={<Trans id="LBL_GA2_APPROVAL_TITLE" />}
            description={<Trans id="LBL_GA2_APPROVAL_DESCRIPTION" />}
            inverted
          />
          <Container pt={0} centerContent>
            <Box
              variants={staggeredAppear}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Card
                variants={appearBelow}
                width="100%"
                title={<Trans id="LBL_GA2_HX50BENEFITS_TITLE" />}
                isInverted
                content={
                  <UnorderedList
                    spacing={4}
                    style={{ color: "white", fontWeight: "medium" }}
                  >
                    <ListItem>
                      <Trans id="LBL_GA2_HX50BENEFIT_1" />
                    </ListItem>
                    <ListItem>
                      <Trans id="LBL_GA2_HX50BENEFIT_2" />
                    </ListItem>
                    <ListItem>
                      <Trans id="LBL_GA2_HX50BENEFIT_3" />
                    </ListItem>
                    <ListItem>
                      <Trans id="LBL_GA2_HX50BENEFIT_4" />
                    </ListItem>
                    <ListItem>
                      <Trans id="LBL_GA2_HX50BENEFIT_5" />
                    </ListItem>
                    <ListItem>
                      <Trans id="LBL_GA2_HX50BENEFIT_6" />
                    </ListItem>
                  </UnorderedList>
                }
              />
              <Box variants={appearBelow}>
                <Text
                  type="p_md"
                  pt={[8, null, 12]}
                  pb={[8, null, 12]}
                  color="white"
                >
                  <Trans id="LBL_GA2_COMPARISON_INTRODUCTION" />
                </Text>
                <Text type="p_md" fontWeight="bold" color="white">
                  <Trans id="LBL_GA2_COMPARISON_TITLE" />
                </Text>
                <UnorderedList px={[0, null, 12]} py={8} spacing={4}>
                  <ListItem pl={4}>
                    <Trans id="LBL_GA2_COMPARISON_ITEM_1" />
                  </ListItem>
                  <ListItem pl={4}>
                    <Trans id="LBL_GA2_COMPARISON_ITEM_2" />
                  </ListItem>
                  <ListItem pl={4}>
                    <Trans id="LBL_GA2_COMPARISON_ITEM_3" />
                  </ListItem>
                  <ListItem pl={4}>
                    <Trans id="LBL_GA2_COMPARISON_ITEM_4" />
                  </ListItem>
                </UnorderedList>
                <Text type="p_md" mt={[8, null, 12]} color="white">
                  <Trans id="LBL_GA2_COMPARISON_CONCLUSION" />
                </Text>
              </Box>
            </Box>
          </Container>
        </Box>
        <VerticalImageSection />
        <TextColumns
          bg="black.900"
          title={<Trans id="LBL_GA2_PROGRAM_TITLE" />}
          description={<Trans id="LBL_GA2_PROGRAM_DESCRIPTION" />}
          inverted
        />
        <Container pb={0} bg="black.900">
          <Box
            width={["100%", null, "80%"]}
            margin="auto"
            variants={staggeredAppear}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            mb={[8, null, 24]}
            bg="gray.900"
          >
            <Box
              bg="green.600"
              color="white"
              variants={appearBelow}
              px={[6, null, 24]}
              py={[6, null, 12]}
              borderLeftColor="primaryLight"
              borderLeftWidth="8px"
            >
              <Text type="title_lg" color="white">
                <Trans id="LBL_GA2_PROGRAM_SECTION_TITLE" />
              </Text>
            </Box>

            <Box
              variants={appearBelow}
              px={[6, null, 24]}
              pt={[6, null, 16]}
              pb={[6, null, 6]}
            >
              <Text type="title_md" color="primary">
                <Trans id="LBL_GA2_PROGRAM_SECTION_TITLE_1" />
              </Text>
              <UnorderedList
                color="white"
                fontWeight="medium"
                spacing={4}
                py={4}
              >
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_1_A" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_1_B" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_1_C" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_1_D" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_1_E" />
                </ListItem>
              </UnorderedList>
            </Box>

            <Box
              variants={appearBelow}
              color="white"
              px={[6, null, 24]}
              py={[6, null, 6]}
            >
              <Text type="title_sm" color="primary">
                <Trans id="LBL_GA2_PROGRAM_SECTION_TITLE_2" />
              </Text>
              <UnorderedList
                color="white"
                fontWeight="medium"
                spacing={4}
                py={4}
              >
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_2_A" />
                </ListItem>
              </UnorderedList>
            </Box>

            <Box
              variants={appearBelow}
              color="white"
              px={[6, null, 24]}
              py={[6, null, 6]}
            >
              <Text type="title_sm" color="primary">
                <Trans id="LBL_GA2_PROGRAM_SECTION_TITLE_3" />
              </Text>
              <UnorderedList
                fontWeight="medium"
                color="white"
                spacing={4}
                py={4}
              >
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_3_A" />
                </ListItem>
              </UnorderedList>
            </Box>

            <Box
              variants={appearBelow}
              color="white"
              px={[6, null, 24]}
              pt={[6, null, 6]}
              pb={[6, null, 16]}
            >
              <Text type="title_sm" color="primary">
                <Trans id="LBL_GA2_PROGRAM_SECTION_TITLE_4" />
              </Text>
              <Text
                type="p_xs"
                textTransform="uppercase"
                color="primary"
                fontWeight="bold"
              >
                <Trans id="LBL_GA2_PROGRAM_SECTION_TITLE_4A" />
              </Text>
              <UnorderedList
                fontWeight="medium"
                color="white"
                spacing={4}
                py={4}
              >
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_4_A" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_4_B" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_4_C" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_4_D" />
                </ListItem>
                <ListItem pl={2}>
                  <Trans id="LBL_GA2_PROGRAM_SECTION_4_E" />
                </ListItem>
              </UnorderedList>
            </Box>
          </Box>
        </Container>
      </Box>
      <ImageSection image="/images/ga/header-2.jpg" bg="black.700" />

      <CardSection
        inverted
        supertitle={<Trans id="LBL_GA2_HX50_ANTETITLE" />}
        large
        title={<Trans id="LBL_GA2_HX50_TITLE" />}
        description={<Trans id="LBL_GA2_HX50_DESCRIPTION" />}
        cardTitle={<Trans id="LBL_GA2_HX50_BENEFITS_TITLE" />}
        cardContent={
          <UnorderedList spacing={4} style={{ color: "white" }}>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_1" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_2" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_3" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_4" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_5" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_6" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_7" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_8" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_9" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_10" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_11" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HX50_BENEFIT_12" />
            </ListItem>
          </UnorderedList>
        }
      />
      <CardSection
        inverted
        overlay
        supertitle={<Trans id="LBL_GA2_HC50_ANTETITLE" />}
        title={<Trans id="LBL_GA2_HC50_TITLE" />}
        description={<Trans id="LBL_GA2_HC50_DESCRIPTION" />}
        cardHeader={<Trans id="LBL_GA2_HC50_FEATURES_TITLE" />}
        cardContent={
          <UnorderedList spacing={4} color="black.900">
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HC50_FEATURE_1" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HC50_FEATURE_2" />
            </ListItem>
            <ListItem pl={2}>
              <Trans id="LBL_GA2_HC50_FEATURE_3" />
            </ListItem>
          </UnorderedList>
        }
        label1={<Trans id="LBL_GA2_HC50_SPEC_LABEL_1" />}
        content1={process.env.hc50_availability}
      />
      <ImageSection image="/images/ga/hx50-vs-hc50.jpg" bg="black.700" />
      {faqCategories.map((cat, index) => (
        <FaqSection
          key={cat.slug}
          items={cat.faqs}
          title={cat.categoryTitle}
          noSubtitle={index > 0}
        />
      ))}
      <NavFooter
        next={<Trans id="LBL_HDC_BUTTON" />}
        pathNext="/digital-cockpit"
        bgNext="/images/nav-footer/hdc.jpg"
        previous={<Trans id="LBL_MENU_ENGINE" />}
        pathPrevious="/gt50-engine"
        bgPrevious="/images/navbar/navbar_gt50.jpg"
      />
      <FloatingCTA show={showCTA} />
    </MainContainer>
  );
}

export async function getServerSideProps({ locale }) {
  const faqCategories = await getFaqsByPage("general-aviation-20", locale);
  const allFaqs = faqCategories.flatMap((cat) => cat.faqs);

  const faqSchemaEntities = allFaqs
    .filter((faq) => faq.question && faq.answerText)
    .map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answerText,
      },
    }));

  const faqSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqSchemaEntities,
  });

  return {
    props: {
      faqCategories,
      faqSchema,
    },
  };
}
